import { passwordStrength as checkPasswordStrength } from "check-password-strength";
import { Card, Form, InputGroup } from "react-bootstrap";
import WarningBanner from "app/storybookComponents/WarningBanner";
import Button from "app/storybookComponents/Button";
import LoginHeader from "./LoginHeader";
import { SyntheticEvent, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  selectUpdatePasswordError,
  selectUpdatePasswordStatus,
  setUpdatePasswordError,
  updateUsersPassword,
} from "./slice";
import { getInputPlaceholder, getInputType, getEyeSlashed } from "./helpers";
import PasswordStrengthBar from "./PasswordStrengthBar";
import { PASSWORD_STRENGTH_OPTIONS } from "./constants";

export default function ResetPasswordFlow() {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const tk1 = searchParams.get("tk1");
  const tk2 = searchParams.get("tk2");

  const [password, setPassword] = useState("");
  const [verifiedPassword, setVerifiedPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showVerifiedPassword, setShowVerifiedPassword] = useState(false);
  const [passwordDoNotMatch, setPasswordDoNotMatch] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<string | null>(null);

  const updatingPasswordStatus = useAppSelector(selectUpdatePasswordStatus);
  const updatePasswordError = useAppSelector(selectUpdatePasswordError);

  const submitHandler = (e: SyntheticEvent) => {
    e.preventDefault();
    if (!tk1 || !tk2) {
      return;
    }

    if (verifiedPassword !== password) {
      setPasswordDoNotMatch(true);
      return;
    }

    dispatch(
      updateUsersPassword({
        tk1,
        tk2,
        password: verifiedPassword,
      })
    );
  };

  const getErrorBanner = () => {
    let errorMessage = "";
    if (passwordDoNotMatch) {
      errorMessage = "Sorry passwords do not match try again";
    } else if (updatePasswordError) {
      errorMessage = updatePasswordError;
    }
    if (!errorMessage) {
      return null;
    }
    return (
      <WarningBanner
        type="light-red"
        className="border-0 d-flex align-items-center gap-2"
      >
        <FontAwesomeIcon icon={["far", "exclamation-triangle"]} />
        <p>{errorMessage}</p>
      </WarningBanner>
    );
  };

  const getUserInputForm = () => (
    <Form
      onSubmit={submitHandler}
      noValidate
      className={"needs-validation column-gap-20px"}
    >
      <Form.Group className="has-error">
        <Form.Label>New password</Form.Label>
        <div style={{ position: "relative" }}>
          <InputGroup>
            <Form.Control
              placeholder={getInputPlaceholder(showPassword)}
              type={getInputType(showPassword)}
              value={password}
              onChange={(e) => {
                const newPassword = e.target.value;
                setPassword(newPassword);

                if (!newPassword) {
                  setPasswordStrength(null);
                  return;
                }

                if (updatePasswordError) {
                  dispatch(setUpdatePasswordError(null));
                }

                const strength = checkPasswordStrength(
                  newPassword,
                  PASSWORD_STRENGTH_OPTIONS
                );
                setPasswordStrength(strength.value);
                setPasswordDoNotMatch(false);
              }}
              className={`${
                passwordDoNotMatch ||
                passwordStrength === "Too weak" ||
                passwordStrength === "Weak"
                  ? "is-invalid "
                  : ""
              }${password ? "" : "empty "} hide-error-icon`}
            />
            <InputGroup.Text>
              <div role="button" onClick={() => setShowPassword(!showPassword)}>
                <FontAwesomeIcon icon={["far", getEyeSlashed(showPassword)]} />
              </div>
            </InputGroup.Text>
          </InputGroup>
          <PasswordStrengthBar passwordStrength={passwordStrength} />
        </div>
        <Form.Text className="text-muted">
          Your new password must be at least 8 characters long
        </Form.Text>
      </Form.Group>

      <Form.Group className="has-error">
        <Form.Label>Confirm password</Form.Label>

        <InputGroup>
          <Form.Control
            placeholder={getInputPlaceholder(showVerifiedPassword)}
            type={getInputType(showVerifiedPassword)}
            value={verifiedPassword}
            onChange={(e) => {
              setVerifiedPassword(e.target.value);
              setPasswordDoNotMatch(false);
            }}
            className={`${passwordDoNotMatch ? "is-invalid " : ""}${
              verifiedPassword ? "" : "empty "
            }`}
          />
          <InputGroup.Text>
            <div
              role="button"
              onClick={() => setShowVerifiedPassword(!showVerifiedPassword)}
            >
              <FontAwesomeIcon
                icon={["far", getEyeSlashed(showVerifiedPassword)]}
              />
            </div>
          </InputGroup.Text>
        </InputGroup>
      </Form.Group>
      {getErrorBanner()}
      <Button
        type="submit"
        className="w-100"
        disabled={
          password.length < 8 ||
          updatingPasswordStatus === "loading" ||
          passwordStrength === "Too weak" ||
          passwordStrength === "Weak" ||
          !!updatePasswordError
        }
      >
        Reset Password
      </Button>
    </Form>
  );

  return (
    <div className="login-page">
      <Card>
        <LoginHeader
          title={"Reset Password"}
          infoText={
            updatingPasswordStatus === "succeeded"
              ? "Your password has been reset"
              : "Enter your new password and confirm."
          }
        />
        {updatingPasswordStatus === "succeeded" ? (
          <Button
            type="submit"
            className="w-100"
            onClick={() => {
              window.location.href = "/";
            }}
          >
            Back to Login
            <FontAwesomeIcon icon="arrow-left" className="ms-2" />
          </Button>
        ) : (
          getUserInputForm()
        )}
      </Card>
    </div>
  );
}
